import React, { useState, useRef, useEffect, useId } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import clsx from 'clsx';
import { motion, AnimatePresence } from 'motion/react';
import { duration } from './primitives/tokens';
import { Portal } from './primitives/Portal';
import { useFloating } from '../../hooks/useFloating';
import { useControllable } from '../../hooks/useControllable';
import { Icon } from './Icon';
import styles from './Select.module.css';
import inputStyles from './Input.module.css';

// ── CVA ──────────────────────────────────────────────────────────────────────

const trigger = cva(styles.trigger, {
  variants: {
    size: {
      sm: styles.sm,
      md: styles.md,
      lg: styles.lg,
    },
  },
  defaultVariants: {
    size: 'md',
  },
});

// ── Types ─────────────────────────────────────────────────────────────────────

type SelectSize = 'sm' | 'md' | 'lg';

interface SelectOption {
  label: React.ReactNode;
  value: string;
  disabled?: boolean;
}

interface SelectProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'>,
  VariantProps<typeof trigger> {
  /** Controlled value */
  value?: string | string[];
  /** Default value */
  defaultValue?: string | string[];
  /** Options */
  options: (string | SelectOption)[];
  /** Change handler */
  onChange?: (value: string | string[]) => void;
  /** Mode */
  mode?: 'single' | 'multiple' | 'tags';
  /**
   * Floating label text. Sits in the trigger at rest; animates upward and
   * shrinks on focus or when the trigger has a selected value. This is the
   * ONLY label/hint API — `placeholder` is intentionally removed.
   */
  label?: string;
  /** Disabled */
  disabled?: boolean;
  /** Loading state */
  loading?: boolean;
  /** Enable search filtering */
  showSearch?: boolean;
  /** Custom filter function */
  filterOption?: (input: string, option: SelectOption) => boolean;
  /** Controlled open */
  open?: boolean;
  /** Show clear button */
  allowClear?: boolean;
  /** Max tags shown in multiple mode */
  maxTagCount?: number;
  /** Size */
  size?: SelectSize;
  /** Background fill variant.
   * - 'default' — surface-container-high grey (standard form fill)
   * - 'plain'   — transparent with subtle border (blends into parent surface)
   * - 'ghost'   — fully transparent, no border (toolbar / inline use) */
  variant?: 'default' | 'plain' | 'ghost';
  /** Show the focus outline on :focus-visible. Defaults to true. Set false when the parent owns the active state. */
  focusEffect?: boolean;
  /** When false, dropdown width is auto (content-sized) rather than locked to trigger width. Default true. */
  popupMatchSelectWidth?: boolean;
}

/**
 * Select — dropdown option selection.
 *
 * | Prop | Type | Default | Description |
 * |------|------|---------|-------------|
 * | value | string \| string[] | — | Controlled value |
 * | defaultValue | string \| string[] | — | Initial value |
 * | options | (string \| SelectOption)[] | — | Options list |
 * | onChange | (value) => void | — | Change handler |
 * | mode | 'single' \| 'multiple' \| 'tags' | 'single' | Selection mode |
 * | label | string | — | Floating label |
 * | disabled | boolean | false | Disable |
 * | loading | boolean | false | Loading spinner |
 * | showSearch | boolean | false | Enable filtering |
 * | filterOption | (input, option) => boolean | — | Custom filter |
 * | allowClear | boolean | false | Show clear button |
 * | maxTagCount | number | — | Max visible tags |
 * | size | 'sm' \| 'md' \| 'lg' | 'md' | Size |
 *
 * @example
 * <Select options={['Apple', 'Banana', 'Cherry']} />
 * <Select mode="multiple" options={[...]} label="Select tags" />
 */
export function Select({
  value: controlledValue,
  defaultValue,
  options,
  onChange,
  mode = 'single',
  label,
  disabled = false,
  loading: _loading = false,
  showSearch = false,
  filterOption,
  open: controlledOpen,
  allowClear = false,
  maxTagCount,
  size = 'md',
  variant = 'default',
  focusEffect = true,
  popupMatchSelectWidth = true,
  style,
  className,
  ...props
}: SelectProps) {
  const isMultiple = mode === 'multiple' || mode === 'tags';
  const defVal = defaultValue ?? (isMultiple ? [] : '');

  const [value, setValue] = useControllable(controlledValue, defVal, onChange);
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = controlledOpen ?? internalOpen;
  const [search, setSearch] = useState('');
  const [highlightIndex, setHighlightIndex] = useState(0);
  const searchRef = useRef<HTMLInputElement>(null);

  const { triggerRef, floatingRef, position } = useFloating<HTMLDivElement, HTMLDivElement>({
    placement: 'bottom-start',
    offset: 4,
    open: isOpen,
  });

  const normalized: SelectOption[] = options.map((opt) => (typeof opt === 'string' ? { label: opt, value: opt } : opt));

  const defaultFilter = (input: string, opt: SelectOption) =>
    String(opt.label).toLowerCase().includes(input.toLowerCase());

  const filtered = search ? normalized.filter((opt) => (filterOption ?? defaultFilter)(search, opt)) : normalized;

  const open = () => {
    if (!disabled) {
      setInternalOpen(true);
      setSearch('');
      setHighlightIndex(0);
    }
  };
  const close = () => {
    setInternalOpen(false);
    setSearch('');
  };

  // Focus search on open
  useEffect(() => {
    if (isOpen && showSearch) {
      setTimeout(() => searchRef.current?.focus(), 0);
    }
  }, [isOpen, showSearch]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (triggerRef.current?.contains(e.target as Node)) return;
      if (floatingRef.current?.contains(e.target as Node)) return;
      close();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, triggerRef, floatingRef]);

  const handleSelect = (optValue: string) => {
    if (isMultiple) {
      const arr = Array.isArray(value) ? value : [];
      const next = arr.includes(optValue) ? arr.filter((v) => v !== optValue) : [...arr, optValue];
      setValue(next);
    } else {
      setValue(optValue);
      close();
    }
  };

  const handleRemoveTag = (optValue: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (Array.isArray(value)) {
      setValue(value.filter((v) => v !== optValue));
    }
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    setValue(isMultiple ? [] : '');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, filtered.length - 1));
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    }
    if (e.key === 'Enter' && filtered[highlightIndex]) {
      handleSelect(filtered[highlightIndex].value);
    }
    if (e.key === 'Escape') close();
  };

  const listboxId = useId();
  const selectedValues = isMultiple ? (Array.isArray(value) ? value : []) : [];
  const singleLabel = !isMultiple && value ? normalized.find((o) => o.value === value)?.label : null;
  const hasValue = isMultiple ? selectedValues.length > 0 : !!value;
  // Floating label is "floated" when the picker is open OR a value is selected.
  const labelFloated = isOpen || hasValue;
  // Default empty-state hint when no caller-provided label.
  const emptyHint = label ?? 'Select...';

  const visibleTags = maxTagCount !== undefined ? selectedValues.slice(0, maxTagCount) : selectedValues;
  const hiddenCount = maxTagCount !== undefined ? Math.max(0, selectedValues.length - maxTagCount) : 0;

  return (
    <div className={clsx(styles.wrapper, className)} style={style} {...props}>
      {/* Trigger */}
      <div
        ref={triggerRef}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        onClick={() => (isOpen ? close() : open())}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        className={clsx(
          trigger({ size }),
          variant === 'plain' && styles.triggerPlain,
          variant === 'ghost' && styles.triggerGhost,
          !focusEffect && styles.triggerNoFocusRing,
          isOpen && styles.open,
          disabled && styles.disabled,
          isMultiple && styles.multipleWrap,
        )}
      >
        {label !== undefined && (
          <label
            className={clsx(
              inputStyles.floatingLabel,
              labelFloated ? inputStyles.labelFloated : inputStyles.labelRest,
              isOpen && inputStyles.labelFocused,
            )}
          >
            {label}
          </label>
        )}
        {isMultiple ? (
          <>
            {visibleTags.map((v) => {
              const opt = normalized.find((o) => o.value === v);
              return (
                <span key={v} className={styles.tag}>
                  {opt?.label ?? v}
                  <button
                    type="button"
                    onClick={(e) => handleRemoveTag(v, e)}
                    aria-label={`Remove ${opt?.label ?? v}`}
                    className={styles.tagRemove}
                  >
                    <Icon name="close" size={10} />
                  </button>
                </span>
              );
            })}
            {hiddenCount > 0 && <span className={styles.hiddenCount}>+{hiddenCount}</span>}
            {selectedValues.length === 0 && !search && label === undefined && (
              <span className={styles.placeholder}>{emptyHint}</span>
            )}
          </>
        ) : (
          <span className={clsx(styles.singleLabel, !singleLabel && styles.placeholder)}>
            {singleLabel ?? (label === undefined ? emptyHint : '')}
          </span>
        )}

        {/* Right icons */}
        <div className={styles.iconsArea}>
          {allowClear && hasValue && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              aria-label="Clear selection"
              className={styles.clearBtn}
            >
              <Icon name="close" size={12} />
            </button>
          )}
          <Icon name="expand_more" size={14} className={clsx(styles.chevron, isOpen && styles.chevronOpen)} />
        </div>
      </div>

      {/* Dropdown */}
      <AnimatePresence>
        {isOpen && (
          <Portal>
            <motion.div
              ref={floatingRef}
              role="listbox"
              id={listboxId}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 4 }}
              transition={{ duration: duration.normal / 1000 }}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              className={styles.dropdown}
              style={{
                '--select-top': typeof position.top === 'number' ? `${position.top}px` : position.top,
                '--select-left': typeof position.left === 'number' ? `${position.left}px` : position.left,
                '--select-width': popupMatchSelectWidth ? `${triggerRef.current?.offsetWidth ?? 0}px` : 'auto',
              } as React.CSSProperties}
            >
            {showSearch && (
              <div className={styles.searchArea}>
                <div className={styles.searchRow}>
                  <Icon name="search" size={14} className={styles.searchIcon} />
                  <input
                    ref={searchRef}
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setHighlightIndex(0);
                    }}
                    onKeyDown={handleKeyDown}
                    placeholder="Search..."
                    aria-label="Search options"
                    className={styles.searchInput}
                  />
                </div>
              </div>
            )}
            {filtered.length === 0 ? (
              <div className={styles.emptyState}>No options</div>
            ) : (
              filtered.map((opt, i) => {
                const isSelected = isMultiple ? selectedValues.includes(opt.value) : value === opt.value;
                return (
                  <SelectItem
                    key={opt.value}
                    option={opt}
                    selected={isSelected}
                    highlighted={i === highlightIndex}
                    multiple={isMultiple}
                    onSelect={() => !opt.disabled && handleSelect(opt.value)}
                    onMouseEnter={() => setHighlightIndex(i)}
                  />
                );
              })
            )}
            </motion.div>
          </Portal>
        )}
      </AnimatePresence>
    </div>
  );
}

function SelectItem({
  option,
  selected,
  highlighted,
  multiple: _multiple,
  onSelect,
  onMouseEnter,
}: {
  option: SelectOption;
  selected: boolean;
  highlighted: boolean;
  multiple: boolean;
  onSelect: () => void;
  onMouseEnter: () => void;
}) {
  return (
    <div
      role="option"
      aria-selected={selected}
      onClick={onSelect}
      onMouseEnter={onMouseEnter}
      className={clsx(
        styles.item,
        highlighted && styles.itemHighlighted,
        selected && styles.itemSelected,
        option.disabled && styles.itemDisabled,
      )}
    >
      <span>{option.label}</span>
      {selected && <Icon name="check" size={14} className={styles.checkIcon} />}
    </div>
  );
}
